import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import classNames from "classnames";
import { Modal } from "components";
import { Loading } from "components/panels";
import React, { useState } from "react";
import { formatShortDate } from "utilities";
import { routes } from "utilities/routing/generatedRoutes";

interface Props {
  documentIndication: server.dto.DocumentIndication
  showId: number
}

const InvoiceIndication = (props: Props) => {
  const { showId, documentIndication } = props;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const infoApi = useQuery({
    queryFn: async () => {
      const response = await window.CoreApi.Invoice.ListInvoiceByShow(showId);
      return response.data?.data;
    },
    queryKey: ["documentIndication", showId, documentIndication.documentType],
    enabled: false,
    retry: false
  });

  const onIndicationClick = () => {
    infoApi.refetch();
    setIsModalOpen(true);
  };

  if (documentIndication.count === 0) {
    return <></>;
  }

  const getClasses = (doc: server.dto.InvoiceForList) => {
    return classNames("s1_small", {
      "s1_tag darkgray-bg": doc.lifeCycle.value === "draft",
      "s1_tag purple-bg": doc.lifeCycle.value === "review",
      "s1_tag orange-bg": doc.lifeCycle.value === "pending" && !doc.overDue,
      "s1_tag red-bg": doc.lifeCycle.value === "pending" && doc.overDue,
      "s1_tag green-bg": doc.lifeCycle.value === "completed",
      "s1_tag gray-bg": doc.lifeCycle.value === "discarded"
    });
  };

  const dontDisplaySendDueDate = (doc: server.dto.InvoiceForList) => {
    return doc.isV2 && (doc.lifeCycle.value !== "completed" && doc.lifeCycle.value !== "discarded" && doc.lifeCycle.value !== "pending");
  };

  return <>
    <div onClick={onIndicationClick} className="documentindication hoverable" style={{ backgroundColor: documentIndication.color }}>
      {documentIndication.documentCode}<sup>{documentIndication.count}</sup>
    </div>

    <Modal
      isOpen={isModalOpen}
      handleClose={() => { setIsModalOpen(false); }}
      title={site.currentEnvironment[documentIndication.documentType]?.plural}
      width="large"
    >
      {(infoApi.isLoading && infoApi.data === undefined) && <Loading />}
      <table className="s1_table list">
        <tbody>
          {infoApi.data?.map(item => (
            <tr key={item.invoiceId}>
              <td style={{ width: 30 }}>
                {!item.isV1 && <i className="icon-rocket" title={RESX.ESign.eSign} />}
              </td>
              <td>
                <div className={classNames({ s1_gray: item.lifeCycle.value === "discarded" })}>
                  <a href={item.isV1
                    ? routes.invoice_Detail(item.invoiceId).absolute
                    : routes.invoiceV2_Detail(item.invoiceId).absolute}>{item.documentNumber}</a>
                </div>
                <div className={getClasses(item)}>
                  {item.lifeCycle.text}
                </div>
              </td>

              <td className={classNames({ s1_gray: item.lifeCycle.value === "discarded" })}>
                {item.addressee.name}
              </td>

              <td style={{ whiteSpace: "nowrap" }} className={classNames({ s1_gray: item.lifeCycle.value === "discarded" })}>
                {!dontDisplaySendDueDate(item) && <>
                  <div>
                    {RESX.DateTime.Send}: {formatShortDate(moment.utc(item.sendDate))}
                  </div>
                  <div className={classNames({
                    s1_red: item.overDue && item.lifeCycle.value !== "completed" && item.lifeCycle.value !== "discarded"
                  })}>
                    {RESX.DateTime.Due}: {formatShortDate(moment.utc(item.dueDate))}
                  </div>
                </>}
              </td>

              <td
                className={classNames("s1_cell_financialamount", { s1_gray: item.lifeCycle.value === "discarded" })}
                style={{ width: 75 }}>
                {item.total?.amount !== 0 && <div>
                  {item.total.formatted}
                </div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Modal>
  </>;
};

export default InvoiceIndication;